import React, { useEffect, useMemo, useRef, useState } from 'react';
import { usePortfolio } from '../context/PortfolioContext';
import { useMarketPrices } from '../context/MarketPriceContext';
import { getProgressBarColor } from '../composables/colorUtils';
import AssistantAccordion from '../components/Assistant/AssistantAccordion';
import ReduceAllocationTool, { ReduceAllocationHolding } from '../components/Assistant/ReduceAllocationTool';
import PriceAlertTool from '../components/Assistant/PriceAlertTool';
import { isCryptoPortfolio } from '../utils/portfolioType';
import { isCryptoSymbol, cryptoPairLabel } from '../composables/fetchCrypto';
import { num } from '../utils/cryptoDecimal';

interface AssistantPageProps {
  userInfo?: any;
}

interface FlatHolding {
  portId: string;
  portName: string;
  subPortId: string;
  subPortName: string;
  symbol: string;
  quantity: number;
  avgCost: number;
  expectedRatio?: number;
}

type ProfitFilter = 'all' | 'gain' | 'loss';
type WeightFilter = 'all' | 'over' | 'under' | 'equal';
type SortOrder = 'desc' | 'asc';

// V2 พอร์ตมี subPorts เป็น array; V1 พอร์ตตัวเองทำหน้าที่เป็น sub-port เดียว (ไม่มี field subPorts)
function getSubPorts(portfolio: any): any[] {
  if (Array.isArray(portfolio.subPorts) && portfolio.subPorts.length) return portfolio.subPorts;
  return [portfolio];
}

function getHoldingsOfSubPort(sub: any): any[] {
  return sub.holdings || sub.stocks || [];
}

function buildFlatHoldings(portfolios: any[]): FlatHolding[] {
  const rows: FlatHolding[] = [];
  portfolios.forEach((p) => {
    const portId = p.portfolioId || p.portId || p.id;
    const portName = p.name || p.portName || 'พอร์ต';
    getSubPorts(p).forEach((sub: any) => {
      const subPortId = sub.subPortId || sub.id || portId;
      const subPortName = sub.name || sub.subPortName || 'พอร์ตย่อย';
      getHoldingsOfSubPort(sub).forEach((item: any) => {
        const symbol = item.stockSymbol || item.assetSymbol || item.symbol;
        const quantity = Number(item.quantity) || 0;
        if (!symbol || quantity <= 0) return;
        const avgCost = Number(item.avgEntryPrice ?? item.avgBuyPrice ?? item.avgCost) || 0;
        const expectedRatio = item.expectedRatio !== undefined && item.expectedRatio !== null ? Number(item.expectedRatio) : undefined;
        rows.push({ portId, portName, subPortId, subPortName, symbol, quantity, avgCost, expectedRatio });
      });
    });
  });
  return rows;
}

// พอร์ตคริปโต: โชว์กล่องเครื่องมือไว้ แต่แจ้งว่ากำลังพัฒนา (ยังไม่เปิดให้ใช้)
const CryptoComingSoon = ({ feature }: { feature: string }) => (
  <div className="flex flex-col items-center justify-center text-center gap-2 py-8 px-4">
    <div
      className="w-12 h-12 rounded-full flex items-center justify-center text-2xl"
      style={{ background: 'linear-gradient(135deg, rgba(247,147,26,0.18), rgba(139,92,246,0.18))' }}
      aria-hidden="true"
    >
      🛠️
    </div>
    <strong className="text-base" style={{ color: 'var(--yellow-text-color)' }}>
      กำลังอยู่ในขั้นตอนการพัฒนา
    </strong>
    <p className="text-sm m-0" style={{ color: 'var(--secondary-text-color)' }}>
      {feature}สำหรับพอร์ตคริปโตยังไม่เปิดให้ใช้งาน
      <br />
      ใช้ได้กับพอร์ตหุ้นตามปกติ
    </p>
  </div>
);

const money = (n: number) => (n < 0 ? '-$' : '$') + Math.abs(n).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

// จำตัวกรอง/หุ้นที่เคยเลือกไว้ใน localStorage เพื่อกลับมาหน้านี้แล้วเป็นแบบเดิม
const ASSISTANT_STATE_STORAGE_KEY = 'assistantPageFilters';

interface SavedAssistantState {
  portfolioFilter?: string;
  subPortFilter?: string;
  profitFilter?: ProfitFilter;
  weightFilter?: WeightFilter;
  sortOrder?: SortOrder;
  selectedKey?: string | null;
  showMoreFilters?: boolean;
}

function loadSavedAssistantState(): SavedAssistantState {
  try {
    const raw = localStorage.getItem(ASSISTANT_STATE_STORAGE_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
}

export default function AssistantPage({ userInfo }: AssistantPageProps) {
  const { userPortfolios, loading, displayMode } = usePortfolio();
  const { currentPrices, fetchPricesForSymbols } = useMarketPrices();

  const saved = useMemo(() => loadSavedAssistantState(), []);

  const [portfolioFilter, setPortfolioFilter] = useState(saved.portfolioFilter ?? 'all');
  const [subPortFilter, setSubPortFilter] = useState(saved.subPortFilter ?? 'all');
  const [profitFilter, setProfitFilter] = useState<ProfitFilter>(saved.profitFilter ?? 'all');
  const [weightFilter, setWeightFilter] = useState<WeightFilter>(saved.weightFilter ?? 'all');
  const [sortOrder, setSortOrder] = useState<SortOrder>(saved.sortOrder ?? 'desc');
  const [selectedKey, setSelectedKey] = useState<string | null>(saved.selectedKey ?? null);
  // "ตัวกรองเพิ่มเติม" เปิดไว้เป็นค่าเริ่มต้น และจำว่าผู้ใช้เปิด/ปิดไว้
  const [showMoreFilters, setShowMoreFilters] = useState(saved.showMoreFilters ?? true);

  // บันทึกตัวกรอง/หุ้นที่เลือกไว้ทุกครั้งที่เปลี่ยน เพื่อให้กลับมาหน้านี้แล้วยังเป็นแบบเดิม
  useEffect(() => {
    try {
      localStorage.setItem(
        ASSISTANT_STATE_STORAGE_KEY,
        JSON.stringify({ portfolioFilter, subPortFilter, profitFilter, weightFilter, sortOrder, selectedKey, showMoreFilters })
      );
    } catch {
      // localStorage อาจใช้ไม่ได้ (private mode ฯลฯ) ไม่ต้อง block การใช้งานหน้านี้
    }
  }, [portfolioFilter, subPortFilter, profitFilter, weightFilter, sortOrder, selectedKey, showMoreFilters]);

  // สลับพอร์ตใหญ่แล้ว พอร์ตย่อยที่เคยเลือกไว้อาจไม่มีอยู่ในพอร์ตใหม่ ต้องรีเซ็ตกลับเป็น "ทั้งหมด"
  // เทียบกับค่าก่อนหน้าจริงๆ (ไม่ใช่แค่ธงรอบแรก) เพราะ React.StrictMode ใน dev จะยิง effect ซ้ำ 2 รอบ
  // ตอน mount ถ้าใช้ธง "รอบแรก" เฉยๆ รอบที่สองจะเข้าใจผิดว่าเปลี่ยนพอร์ตจริง แล้วไปเคลียร์ subPortFilter
  // ที่เพิ่ง restore จาก localStorage ทิ้งทันที
  const prevPortfolioFilterRef = useRef(portfolioFilter);
  useEffect(() => {
    if (prevPortfolioFilterRef.current !== portfolioFilter) {
      prevPortfolioFilterRef.current = portfolioFilter;
      setSubPortFilter('all');
    }
  }, [portfolioFilter]);

  const selectedPortfolio = useMemo(
    () => (userPortfolios || []).find((p: any) => (p.portfolioId || p.portId || p.id) === portfolioFilter) || null,
    [userPortfolios, portfolioFilter]
  );

  const subPortOptions = useMemo(() => {
    if (portfolioFilter === 'all' || !selectedPortfolio) return [];
    const portId = selectedPortfolio.portfolioId || selectedPortfolio.portId || selectedPortfolio.id;
    return getSubPorts(selectedPortfolio).map((sub: any, idx: number) => ({
      subPortId: sub.subPortId || sub.id || portId,
      subPortName: sub.name || sub.subPortName || `พอร์ตย่อย ${idx + 1}`,
    }));
  }, [portfolioFilter, selectedPortfolio]);

  const flatHoldings = useMemo(() => buildFlatHoldings(userPortfolios || []), [userPortfolios]);
  const holdingSymbols = useMemo(() => Array.from(new Set(flatHoldings.map((h) => h.symbol))), [flatHoldings]);

  useEffect(() => {
    if (holdingSymbols.length) fetchPricesForSymbols(holdingSymbols);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [holdingSymbols.join(',')]);

  // เช็คว่าพอร์ต/พอร์ตย่อย/หุ้นที่ restore มาจาก localStorage ยังมีอยู่จริงไหม "หลังข้อมูลนิ่งแล้วเท่านั้น"
  // (debounce 800ms) เพราะแค่เช็ค userPortfolios.length>0 ครั้งแรกไม่พอ — ช่วงโหลดหน้าแรกอาจมี fetch
  // ซ้อนกันหลายรอบ (เช่น PortfolioContext ยิง refreshUserPortfolios จากสอง useEffect พร้อมกัน บวกกับ
  // React.StrictMode เรียกซ้ำอีกใน dev) ทำให้ userPortfolios รอบแรกๆ ที่ไม่ว่างแต่ยังไม่ใช่ข้อมูลตัวเต็ม
  // (เช่น ยังไม่มีหุ้นที่เพิ่งเลือกไว้) ถูกเข้าใจผิดว่าเป็นข้อมูลสุดท้ายแล้วเคลียร์ selection ทิ้งไปก่อนเวลาอันควร
  // ต้องรอให้ไม่มี fetch ใหม่เข้ามาเปลี่ยน userPortfolios/loading อีกภายใน 800ms ก่อนค่อยเชื่อว่าโหลดเสร็จจริง
  const hasReconciledRestoredState = useRef(false);
  useEffect(() => {
    if (hasReconciledRestoredState.current) return;
    const timer = setTimeout(() => {
      if (loading || hasReconciledRestoredState.current) return;
      if (!userPortfolios || userPortfolios.length === 0) return; // ยังไม่มีข้อมูลจริง รอรอบถัดไป
      hasReconciledRestoredState.current = true;

      const portfolioExists =
        portfolioFilter === 'all' ||
        userPortfolios.some((p: any) => (p.portfolioId || p.portId || p.id) === portfolioFilter);

      if (!portfolioExists) {
        setPortfolioFilter('all');
        setSubPortFilter('all');
      } else if (portfolioFilter !== 'all' && subPortFilter !== 'all') {
        const matchedPortfolio = userPortfolios.find((p: any) => (p.portfolioId || p.portId || p.id) === portfolioFilter);
        const matchedPortId = matchedPortfolio.portfolioId || matchedPortfolio.portId || matchedPortfolio.id;
        const subPortExists = getSubPorts(matchedPortfolio).some(
          (sub: any) => (sub.subPortId || sub.id || matchedPortId) === subPortFilter
        );
        if (!subPortExists) setSubPortFilter('all');
      }

      if (selectedKey && !flatHoldings.some((h) => `${h.portId}__${h.subPortId}__${h.symbol}` === selectedKey)) {
        setSelectedKey(null);
      }
    }, 800);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, userPortfolios, flatHoldings, portfolioFilter, subPortFilter, selectedKey]);

  const portfolioHoldings = useMemo(() => {
    let list = flatHoldings;
    if (portfolioFilter !== 'all') list = list.filter((h) => h.portId === portfolioFilter);
    if (portfolioFilter !== 'all' && subPortFilter !== 'all') list = list.filter((h) => h.subPortId === subPortFilter);
    return list;
  }, [flatHoldings, portfolioFilter, subPortFilter]);

  // เครื่องมือลดสัดส่วนต้องรู้ subPortId ที่ชัดเจนหนึ่งเดียว (เป้า/สัดส่วนของ backend คิดแยกต่อพอร์ตย่อย)
  // ดังนั้นต้องเลือกทั้งพอร์ตใหญ่และพอร์ตย่อยให้ครบก่อน ไม่ใช่แค่พอร์ตใหญ่อย่างเดียว
  const isScopedToOneSubPort = portfolioFilter !== 'all' && subPortFilter !== 'all';

  // "สัดส่วนเทียบเป้า" เทียบกับ target ของพอร์ตย่อยเดียวเท่านั้น เหมือนเกจพลัง จึงปิดใช้งาน/รีเซ็ตเมื่อยังไม่ได้ scope ให้แคบพอ
  useEffect(() => {
    if (!isScopedToOneSubPort) setWeightFilter('all');
  }, [isScopedToOneSubPort]);

  const totalMarketValue = useMemo(
    () => portfolioHoldings.reduce((sum, h) => sum + h.quantity * (currentPrices[h.symbol] || 0), 0),
    [portfolioHoldings, currentPrices]
  );

  const allRows = useMemo(
    () =>
      portfolioHoldings.map((h) => {
        const price = currentPrices[h.symbol] || 0;
        const marketValue = h.quantity * price;
        const costValue = h.quantity * h.avgCost;
        const profit = marketValue - costValue;
        const pl: 'gain' | 'loss' | 'flat' = Math.abs(profit) < 1e-8 ? 'flat' : profit > 0 ? 'gain' : 'loss';
        return {
          ...h,
          price,
          marketValue,
          costValue,
          weightPct: totalMarketValue > 0 ? (marketValue / totalMarketValue) * 100 : 0,
          profit,
          profitPct: costValue > 0 ? (profit / costValue) * 100 : 0,
          pl,
          key: `${h.portId}__${h.subPortId}__${h.symbol}`,
        };
      }),
    [portfolioHoldings, currentPrices, totalMarketValue]
  );

  const rows = useMemo(() => {
    const EPS = 0.005; // ตรงกับความละเอียดที่แสดงผล 2 ตำแหน่งทศนิยม
    const filtered = allRows.filter((r) => {
      if (profitFilter !== 'all' && r.pl !== profitFilter) return false;
      if (isScopedToOneSubPort && weightFilter !== 'all') {
        const diff = r.weightPct - (r.expectedRatio ?? 0);
        if (weightFilter === 'over' && !(diff > EPS)) return false;
        if (weightFilter === 'under' && !(diff < -EPS)) return false;
        if (weightFilter === 'equal' && !(Math.abs(diff) <= EPS)) return false;
      }
      return true;
    });
    return [...filtered].sort((a, b) =>
      sortOrder === 'desc' ? b.weightPct - a.weightPct : a.weightPct - b.weightPct
    );
  }, [allRows, profitFilter, weightFilter, sortOrder, isScopedToOneSubPort]);

  const totalProfit = useMemo(() => allRows.reduce((sum, r) => sum + r.profit, 0), [allRows]);
  const totalCostBasis = useMemo(() => allRows.reduce((sum, r) => sum + r.costValue, 0), [allRows]);
  const totalProfitPct = totalCostBasis > 0 ? (totalProfit / totalCostBasis) * 100 : 0;

  const rowKeys = rows.map((r) => r.key).join(',');
  useEffect(() => {
    // รอให้ effect เช็คของที่ restore มาจาก localStorage (ด้านบน) ยืนยันก่อนว่าโหลดข้อมูลจริงเสร็จแล้ว
    // ไม่งั้นช่วงที่ loading เพิ่งเป็น false แต่ userPortfolios ยังว่างอยู่ชั่วคราว (เช่น React.StrictMode
    // เรียก fetch ซ้ำตอน dev) จะทำให้ rows ว่างชั่วคราวไปด้วย แล้วเข้าใจผิดว่าหุ้นที่เลือกไว้หายไป ทั้งที่จริงๆ
    // ข้อมูลยังมาไม่ครบ กลายเป็นเด้งไปเลือกตัวแรกแทนหุ้นที่เคยเลือกไว้
    if (loading || !hasReconciledRestoredState.current) return;
    if (rows.length && !rows.some((r) => r.key === selectedKey)) {
      setSelectedKey(rows[0].key);
    } else if (!rows.length) {
      setSelectedKey(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rowKeys, loading]);

  const selectedRow = rows.find((r) => r.key === selectedKey) || null;

  // แจ้งเตือนแนวรับ–แนวต้านยังไม่รองรับคริปโต (price-alerts) จึงแสดง "กำลังพัฒนา" แทน
  // เมื่อเลือกพอร์ตคริปโต หรือเลือกเหรียญจากมุมมอง "ทุกพอร์ต" / ส่วนลดสัดส่วนใช้ได้ทั้งหุ้นและคริปโต
  const selectedRowPortfolio = selectedRow
    ? (userPortfolios || []).find((p: any) => (p.portfolioId || p.portId || p.id) === selectedRow.portId)
    : null;
  const isCryptoSelection =
    isCryptoPortfolio(selectedPortfolio) ||
    isCryptoPortfolio(selectedRowPortfolio) ||
    isCryptoSymbol(selectedRow?.symbol);

  const reduceAllocationHolding: ReduceAllocationHolding | null = selectedRow && isScopedToOneSubPort
    ? {
        portId: selectedRow.portId,
        portName: selectedRow.portName,
        subPortId: selectedRow.subPortId,
        subPortName: selectedRow.subPortName,
        symbol: selectedRow.symbol,
        quantity: selectedRow.quantity,
        avgCost: selectedRow.avgCost,
        currentPrice: selectedRow.price,
        weightPct: selectedRow.weightPct,
        expectedRatio: selectedRow.expectedRatio,
        isCrypto: isCryptoSelection,
      }
    : null;

  const plTint = (pl: 'gain' | 'loss' | 'flat', isSelected: boolean) => {
    if (pl === 'gain') {
      return {
        background: isSelected ? 'rgba(34, 197, 94, 0.22)' : 'rgba(34, 197, 94, 0.12)',
        borderColor: isSelected ? 'var(--yellow-text-color)' : 'rgba(34, 197, 94, 0.35)',
      };
    }
    if (pl === 'loss') {
      return {
        background: isSelected ? 'rgba(239, 68, 68, 0.22)' : 'rgba(239, 68, 68, 0.12)',
        borderColor: isSelected ? 'var(--yellow-text-color)' : 'rgba(239, 68, 68, 0.35)',
      };
    }
    return {
      background: isSelected ? 'var(--hover-item)' : 'var(--bg-item)',
      borderColor: isSelected ? 'var(--yellow-text-color)' : 'transparent',
    };
  };

  return (
    <div className="w-full p-4 md:p-6 min-h-screen" style={{ color: 'var(--text-color)' }}>
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <h2 className="text-xl font-bold">ผู้ช่วยลงทุน</h2>
        <span className="text-sm px-2 py-1 rounded-full" style={{ background: 'var(--bg-item)', color: 'var(--text-color)' }}>
          อิงราคาตลาดล่าสุด · อัปเดตทุก 15 วินาที
        </span>
      </div>

      {loading ? (
        <div className="flex justify-center items-center h-40">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-yellow-400"></div>
          <span className="ml-3" style={{ color: 'var(--text-color)' }}>กำลังโหลดข้อมูลพอร์ต...</span>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-[55fr_45fr] gap-[18px] items-start">
          {/* ซ้าย: หุ้นที่ลงทุนอยู่ · สัดส่วน 55/45 ให้ตรงกับ mockup (wi-left/wi-right) */}
          <div className="rounded-xl overflow-hidden" style={{ background: 'var(--component-bg-color)', border: '1px solid var(--bg-item)' }}>
            <div className="p-4" style={{ borderBottom: '1px solid var(--bg-item)' }}>
              <div className="flex items-center justify-between gap-2 mb-3">
                <h3 className="font-bold text-sm md:text-base">หุ้นที่ลงทุนอยู่</h3>
                <span className="text-base px-2 py-1 rounded-full" style={{ background: 'var(--bg-item)', color: 'var(--text-color)' }}>
                  {allRows.length} ตัว
                </span>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 items-start">
                <div className="flex flex-col gap-1.5">
                  <label className="block text-base" style={{ color: 'var(--text-color)' }}>
                    เลือกพอร์ต
                    <select
                      value={portfolioFilter}
                      onChange={(e) => setPortfolioFilter(e.target.value)}
                      className="mt-1 w-full rounded-lg px-2 py-2 text-sm outline-none"
                      style={{ background: 'var(--bg-item)', color: 'var(--text-color)', border: '1px solid var(--text-input)' }}
                    >
                      <option value="all">พอร์ตทั้งหมด ({(userPortfolios || []).length} พอร์ต)</option>
                      {(userPortfolios || []).map((p: any) => {
                        const id = p.portfolioId || p.portId || p.id;
                        return (
                          <option key={id} value={id}>
                            {p.name || p.portName || id}
                          </option>
                        );
                      })}
                    </select>
                  </label>

                  <label className="block text-base" style={{ color: 'var(--text-color)' }}>
                    เลือกพอร์ตย่อย
                    <select
                      value={subPortFilter}
                      onChange={(e) => setSubPortFilter(e.target.value)}
                      disabled={portfolioFilter === 'all'}
                      className="mt-1 w-full rounded-lg px-2 py-2 text-sm outline-none disabled:opacity-50"
                      style={{ background: 'var(--bg-item)', color: 'var(--text-color)', border: '1px solid var(--text-input)' }}
                    >
                      <option value="all">
                        {portfolioFilter === 'all' ? 'เลือกพอร์ตก่อน' : `พอร์ตย่อยทั้งหมด (${subPortOptions.length})`}
                      </option>
                      {subPortOptions.map((s) => (
                        <option key={s.subPortId} value={s.subPortId}>
                          {s.subPortName}
                        </option>
                      ))}
                    </select>
                  </label>
                </div>

                <div className="text-right">
                  <span className="text-sm block" style={{ color: 'var(--text-color)' }}>
                    มูลค่ารวม (หุ้นที่ถือ)
                  </span>
                  <strong className="text-3xl block">{money(totalMarketValue)}</strong>
                  {/* กล่อง "กำไร/ขาดทุน ทิพย์" ยกมาจาก PortfolioAllocationBoxV2.tsx (กล่อง 2 เวอร์ชัน desktop)
                      แทนที่บรรทัดข้อความกำไร/ขาดทุนแบบเดิม */}
                  {totalCostBasis > 0 && (() => {
                    const isProfitLocal = totalProfit >= 0;
                    const borderClass = isProfitLocal ? 'border-green-500/50' : 'border-red-500/50';
                    const bgClass = isProfitLocal ? 'bg-green-500/10' : 'bg-red-500/10';
                    const textClass = isProfitLocal ? 'text-green-500' : 'text-red-500';
                    const trendPath = isProfitLocal ? 'M13 7h8m0 0v8m0-8l-8 8-4-4-6 6' : 'M13 17h8m0 0v-8m0 8l-8-8-4 4-6-6';
                    return (
                      <div
                        className={`mt-2 inline-flex items-start gap-2 pl-4 pr-8 py-3 rounded-xl border-2 ${borderClass} shadow-sm`}
                        style={{ background: 'var(--component-bg-color)' }}
                      >
                        <div className={`p-1.5 ${bgClass} rounded-lg shrink-0`}>
                          <svg className={`w-4 h-4 ${textClass}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d={trendPath}></path>
                          </svg>
                        </div>
                        <div className="text-left">
                          <div className="text-xs font-bold" style={{ color: 'var(--text-color)' }}>
                            กำไร/ขาดทุน "ทิพย์"
                          </div>
                          <div className={`text-base font-bold ${textClass}`}>
                            {isProfitLocal ? '+' : '-'}{money(Math.abs(totalProfit))}
                          </div>
                          <div className={`text-[11px] font-bold ${textClass} flex items-center gap-1`}>
                            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d={trendPath}></path>
                            </svg>
                            ({isProfitLocal ? '+' : '-'}{Math.abs(totalProfitPct).toFixed(2)}%)
                          </div>
                        </div>
                      </div>
                    );
                  })()}
                </div>
              </div>

              <details
                className="group mt-3"
                style={{ borderTop: '1px solid var(--bg-item)' }}
                open={showMoreFilters}
                onToggle={(e) => setShowMoreFilters(e.currentTarget.open)}
              >
                <summary
                  className="pt-3 pb-1 text-sm font-semibold cursor-pointer select-none flex items-center gap-2 list-none [&::-webkit-details-marker]:hidden"
                  style={{ color: 'var(--text-color)' }}
                >
                  <span>ตัวกรองเพิ่มเติม</span>
                  <span className="text-xs transition-transform group-open:rotate-180" style={{ color: 'var(--yellow-text-color)' }}>
                    ▾
                  </span>
                </summary>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-2">
                <label className="block text-base" style={{ color: 'var(--text-color)' }}>
                  กำไร / ขาดทุนที่ยังไม่ขาย
                  <select
                    value={profitFilter}
                    onChange={(e) => setProfitFilter(e.target.value as ProfitFilter)}
                    className="mt-1 w-full rounded-lg px-2 py-2 text-sm outline-none"
                    style={{ background: 'var(--bg-item)', color: 'var(--text-color)', border: '1px solid var(--text-input)' }}
                  >
                    <option value="all">ทุกสถานะ</option>
                    <option value="gain">สีเขียว · กำไร</option>
                    <option value="loss">สีแดง · ขาดทุน</option>
                  </select>
                </label>
                <label className="block text-base" style={{ color: 'var(--text-color)' }}>
                  สัดส่วนเทียบเป้า
                  <select
                    value={weightFilter}
                    onChange={(e) => setWeightFilter(e.target.value as WeightFilter)}
                    disabled={!isScopedToOneSubPort}
                    className="mt-1 w-full rounded-lg px-2 py-2 text-sm outline-none disabled:opacity-50"
                    style={{ background: 'var(--bg-item)', color: 'var(--text-color)', border: '1px solid var(--text-input)' }}
                  >
                    <option value="all">ทุกสัดส่วน</option>
                    <option value="over">เกินเป้า</option>
                    <option value="under">ต่ำกว่าเป้า</option>
                    <option value="equal">เท่ากับเป้า</option>
                  </select>
                </label>
                <div className="block text-base sm:col-span-2" style={{ color: 'var(--text-color)' }}>
                  เรียงตามสัดส่วน
                  <button
                    type="button"
                    onClick={() => setSortOrder(sortOrder === 'desc' ? 'asc' : 'desc')}
                    className="mt-1 w-full flex items-center justify-between gap-2 rounded-lg px-3 py-2 text-sm font-semibold"
                    style={{ background: 'var(--bg-item)', color: 'var(--text-color)', border: '1px solid var(--text-input)' }}
                  >
                    <span>{sortOrder === 'desc' ? 'มาก → น้อย' : 'น้อย → มาก'}</span>
                    <svg
                      className="w-4 h-4 flex-shrink-0 transition-transform duration-200"
                      style={{ transform: sortOrder === 'asc' ? 'rotate(180deg)' : 'none', color: 'var(--yellow-text-color)' }}
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                      aria-hidden="true"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M12 5v14m0 0l-6-6m6 6l6-6" />
                    </svg>
                  </button>
                </div>
              </div>
              </details>
            </div>

            {selectedRow && (
              <div
                className="px-4 py-3 flex items-center justify-between gap-3 flex-wrap text-sm"
                style={{ borderBottom: '1px solid var(--bg-item)' }}
              >
                <strong style={{ color: 'var(--yellow-text-color)' }}>{cryptoPairLabel(selectedRow.symbol)}</strong>
                <span style={{ color: 'var(--text-color)' }}>
                  เฉลี่ย <b style={{ color: 'var(--text-color)' }}>{money(selectedRow.avgCost)}</b>
                </span>
                <span style={{ color: 'var(--text-color)' }}>
                  มูลค่า <b style={{ color: 'var(--text-color)' }}>{money(selectedRow.marketValue)}</b>
                </span>
              </div>
            )}

            {rows.length === 0 ? (
              <div className="px-4 py-10 text-center text-sm" style={{ color: 'var(--text-color)' }}>
                ไม่มีหุ้นตรงกับตัวกรองที่เลือก
              </div>
            ) : (
              <div className="p-3 grid grid-cols-2 gap-2 max-h-[520px] overflow-y-auto sm:[grid-template-columns:repeat(auto-fit,minmax(96px,1fr))]">
                {rows.map((r) => {
                  const isSelected = r.key === selectedKey;
                  const tint = plTint(r.pl, isSelected);
                  // เกจสัดส่วนเทียบเป้าใช้ได้เฉพาะตอนดูพอร์ตย่อยเดียว เพราะ expectedRatio กำหนดไว้ต่อพอร์ตย่อย
                  // แสดงเสมอเมื่อ scope ชัดเจนแล้ว (ไม่เช็ค > 0) ให้ตรงกับ PortfolioStocksBox.tsx ที่โชว์ "เป้าหมาย: 0%" ได้เช่นกัน
                  const showTargetGauge = isScopedToOneSubPort;
                  const expectedRatio = r.expectedRatio ?? 0;
                  const gaugePct = showTargetGauge ? (r.weightPct / expectedRatio) * 100 : 0;
                  const gaugeColor = showTargetGauge ? getProgressBarColor(gaugePct, expectedRatio) : undefined;
                  return (
                    <button
                      key={r.key}
                      type="button"
                      onClick={() => setSelectedKey(r.key)}
                      className="text-left rounded-lg px-2.5 py-2 text-sm"
                      style={{ background: tint.background, border: `1px solid ${tint.borderColor}` }}
                    >
                      <div className="flex items-center justify-between gap-1">
                        <strong className="text-[13px]">{cryptoPairLabel(r.symbol)}</strong>
                        <span style={{ color: showTargetGauge ? gaugeColor : undefined }}>{r.weightPct.toFixed(2)}%</span>
                      </div>
                      {showTargetGauge ? (
                        <>
                          <div className="h-1.5 w-full rounded-full overflow-hidden mt-1.5" style={{ background: 'var(--bg-item)' }}>
                            <div
                              className="h-full rounded-full"
                              style={{ width: `${Math.min(gaugePct, 100) || 0}%`, background: gaugeColor }}
                            />
                          </div>
                          <div className="mt-1" style={{ color: 'var(--text-color)' }}>
                            เป้า {expectedRatio.toFixed(2)}%
                          </div>
                        </>
                      ) : (
                        <div className="mt-1" style={{ color: 'var(--text-color)' }}>
                          {portfolioFilter === 'all' ? 'ของพอร์ตทั้งหมด' : 'ของพอร์ตนี้'}
                        </div>
                      )}
                      <div className="mt-1" style={{ color: r.pl === 'gain' ? 'var(--green-text-color)' : r.pl === 'loss' ? 'var(--red-text-color)' : 'var(--text-color)' }}>
                        {r.profit >= 0 ? 'กำไร +' : 'ขาดทุน -'}
                        {money(Math.abs(r.profit))}
                        <br />({r.profit >= 0 ? '+' : '-'}
                        {Math.abs(r.profitPct).toFixed(2)}%)
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          {/* ขวา: เครื่องมือ */}
          <div className="flex flex-col gap-[18px]">
            {/* คริปโตใช้ได้แล้ว: ReduceAllocationTool ส่งราคาตรงตัวผ่าน decimalValue เมื่อ holding.isCrypto */}
            <AssistantAccordion title="ขายเพื่อลดสัดส่วน" badge="คำนวณเท่านั้น ไม่ส่งคำสั่งขาย">
              <ReduceAllocationTool
                holding={reduceAllocationHolding}
                portfolioVersion={displayMode === 'V1' ? 'V1' : 'V2'}
                placeholderMessage={!isScopedToOneSubPort ? 'โปรดเลือกพอร์ตย่อยก่อน' : undefined}
                // พอร์ตคริปโตอาจส่งยอดเงินสดเป็น string → แปลงเป็นตัวเลขเพื่อประมาณ "เงินสดหลังขาย" เท่านั้น
                currentCash={num(selectedPortfolio?.summary?.cashValue ?? selectedPortfolio?.cashBalances?.[0]?.amount ?? 0)}
              />
            </AssistantAccordion>

            <AssistantAccordion title="แจ้งเตือนแนวรับ–แนวต้าน" badge={isCryptoSelection ? 'กำลังพัฒนา' : 'Telegram · ทดลองตั้งเตือน'}>
              {isCryptoSelection ? (
                <CryptoComingSoon feature="การแจ้งเตือนแนวรับ–แนวต้าน" />
              ) : (
                <PriceAlertTool symbol={selectedRow?.symbol} />
              )}
            </AssistantAccordion>
          </div>
        </div>
      )}
    </div>
  );
}
